import PropTypes from 'prop-types'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setPrecio } from '../../slices/filterSlice'
import ComponenteError from '../publicaciones/ComponenteError'
import { Filtros } from '../reutilizables/filtros/Filtros'
import { Paginacion } from '../reutilizables/paginacion/Paginacion'
import { Pedido } from '../reutilizables/pedidos/Pedido'
import * as pedidosService from '../servicios/pedidosService'
import { buscarMedioPago } from '../servicios/publicacionesService'
import * as ventasService from '../servicios/ventasService'
import BordePrincipal from '../ui/BordePrincipal'
import styles from './Pedidos.module.css'

const PAGINA_ITEMS = 15

const Pedidos = ({ esVentas }) => {
  const topRef = useRef(null)
  const [itemsPorPagina, setItemsPorPagina] = useState(PAGINA_ITEMS)
  const [paginaActual, setPaginaActual] = useState(1)
  const usuario = parseInt(localStorage.getItem('idUsuario'))
  const [pedidos, setPedidos] = useState([])
  const [sinResultados, setSinResultados] = useState(false)
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [mediosPago, setMediosPago] = useState([])
  const [filtros, setFiltros] = useState({
    filtroVendedor: null,
    filtroEstado: null,
    filtroComprador: null
  })
  const dispatch = useDispatch()
  const { precioDesde, precioHasta } = useSelector((state) => state.filters)
  const [filtrosFijos, setFiltrosFijos] = useState([])
  const [pedidosOriginales, setPedidosOriginales] = useState([])
  const [fechaDesde, setFechaDesde] = useState(null)
  const [fechaHasta, setFechaHasta] = useState(null)

  const fetchData = async (search) => {
    const { data } = await pedidosService.buscarPorUsuario(usuario, search)
    const dataProcesada = data.map((pedido) => ({
      ...pedido,
      pedidoItems: pedido.pedidoItems.map((item) => ({
        ...item,
        medioPago: obtenerMedioPago(item.idMedioPago)
      }))
    }))
    setPedidos(dataProcesada)
    setPedidosOriginales(dataProcesada)
    setSinResultados(data.length === 0)
  }

  const cargarMediosPago = async () => {
    const { data } = await buscarMedioPago()
    setMediosPago(data || [])
  }

  const obtenerMedioPago = (idMedioPago) => {
    if (!mediosPago || mediosPago.length === 0) {
      return ''
    }

    const medioPago = mediosPago.find((mp) => mp.idMedioPago === idMedioPago)
    return medioPago?.nombre || ''
  }

  const fetchPedidos = async (search) => {
    const { data } = await ventasService.buscarPorUsuario(usuario, search)
    const pedidosProcesados = data
      .map((pedido) => ({
        ...pedido,
        pedidoItems: Array.isArray(pedido.ventaItems)
          ? pedido.ventaItems.map((element) => ({
              ...element,
              alicuota: element.alicuota,
              cantidad: element.cantidad,
              estados: pedido.estados,
              idPedido: pedido.idPedido,
              compradorNombre: element.nombre,
              vendedorNombre: element.nombre,
              vendedorEMail: element.email,
              vendedorTelefono: element.telefono,
              vendedorCalle: element.calle,
              vendedorCalleAltura: element.calleAltura,
              vendedorPiso: element.piso,
              vendedorCP: element.cp,
              descripcionEstado: element.estadoDescripcion,
              entregaLocalidad: element.localidad,
              entregaCalle: element.calle,
              entregaCalleAltura: element.calleAltura,
              entregaProvincia: element.provincia,
              medioPago: obtenerMedioPago(element.idMedioPago),
              precioEnvio: element.precioEnvio ?? pedido.precioEnvio ?? 0,
              envioTipo: element.envioTipo ?? pedido.envioTipo ?? '',
              idMedioEnvio: element.idMedioEnvio ?? pedido.idMedioEnvio ?? null
            }))
          : []
      }))
      .sort(
        (a, b) =>
          new Date(b.ventaItems?.[0]?.fechaPedido || 0) -
          new Date(a.ventaItems?.[0]?.fechaPedido || 0)
      )

    setPedidos(pedidosProcesados)
    setPedidosOriginales(pedidosProcesados)
    setSinResultados(pedidosProcesados.length === 0)
  }

  const handleBuscar = async () => {
    if (esVentas) {
      await fetchPedidos('')
    } else {
      await fetchData('')
    }
    setPaginaActual(1)
  }

  useEffect(() => {
    if (mediosPago.length !== 0) {
      handleBuscar()
    }
  }, [mediosPago, esVentas])

  useEffect(() => {
    cargarMediosPago()
  }, [])

  const totalPaginas = Math.ceil(pedidos.length / itemsPorPagina)

  const indexInicio = (paginaActual - 1) * itemsPorPagina
  const indexFin = indexInicio + itemsPorPagina
  const pedidosPaginados = pedidos.slice(indexInicio, indexFin)

  const pedidosConItems = useMemo(
    () =>
      pedidosOriginales.filter(
        (p) => Array.isArray(p.pedidoItems) && p.pedidoItems.length > 0
      ),
    [pedidosOriginales]
  )

  const vendedoresUnicos = useMemo(
    () => [
      ...new Map(
        pedidosConItems.map((p) => [
          p.idVendedor,
          {
            label: p.pedidoItems[0].vendedorNombre,
            valor: p.idVendedor
          }
        ])
      ).values()
    ],
    [pedidosConItems]
  )

  const compradoresUnicos = useMemo(
    () => [
      ...new Map(
        pedidosConItems.map((p) => [
          p.idComprador,
          {
            label: p.pedidoItems[0].compradorNombre,
            valor: p.idComprador
          }
        ])
      ).values()
    ],
    [pedidosConItems]
  )

  const estadosUnicos = useMemo(
    () => [
      ...new Map(
        pedidosConItems.flatMap(({ pedidoItems }) =>
          pedidoItems.map((item) => [
            item.idPedidoItemEstado,
            {
              valor: item.idPedidoItemEstado,
              label: item.estadoNombre
            }
          ])
        )
      ).values()
    ],
    [pedidosConItems]
  )

  const toggleFiltro = useCallback((key, value) => {
    const valorNormalizado = value === '' || value === undefined ? null : value

    setFiltros((prev) => ({
      ...prev,
      [key]: prev[key] === valorNormalizado ? null : valorNormalizado
    }))
  }, [])

  useEffect(() => {
    setFiltrosFijos([
      ...(!esVentas
        ? [
            {
              titulo: 'Vendedor',
              value: filtros.filtroVendedor,
              opciones: vendedoresUnicos,
              onChange: (v) => toggleFiltro('filtroVendedor', v)
            }
          ]
        : [
            {
              titulo: 'Comprador',
              value: filtros.filtroComprador,
              opciones: compradoresUnicos,
              onChange: (v) => toggleFiltro('filtroComprador', v)
            }
          ]),
      {
        titulo: 'Estado',
        value: filtros.filtroEstado,
        opciones: estadosUnicos,
        onChange: (v) => toggleFiltro('filtroEstado', v)
      }
    ])
  }, [
    esVentas,
    filtros.filtroVendedor,
    filtros.filtroComprador,
    filtros.filtroEstado,
    vendedoresUnicos,
    compradoresUnicos,
    estadosUnicos,
    toggleFiltro
  ])

  const fechaIso = (fecha) => {
    return fecha ? fecha.toISOString() : null
  }

  const aplicarFiltros = () => {
    const desde = fechaDesde ? new Date(fechaDesde) : null
    const hasta = fechaHasta ? new Date(fechaHasta) : null
    const valorBusqueda = (textoBusqueda || '').trim().toLowerCase()

    const pedidosFiltrados = pedidosOriginales.filter((p) => {
      if (filtros.filtroVendedor && p.idVendedor !== filtros.filtroVendedor) {
        return false
      }

      if (
        filtros.filtroEstado !== null &&
        !p.pedidoItems.some((item) => item.idPedidoItemEstado === filtros.filtroEstado)
      ) {
        return false
      }

      if (filtros.filtroComprador && p.idComprador !== filtros.filtroComprador) {
        return false
      }

      if (desde || hasta) {
        const fecha = new Date(p?.ventaItems?.[0]?.fechaPedido ?? p.fechaPedido)

        // Fecha inválida o por defecto (0001)
        if (isNaN(fecha.getTime())) {
          return false
        }

        const desdeISO = fechaIso(desde)
        const hastaISO = fechaIso(hasta)
        const fechaISO = fechaIso(fecha)

        if (desdeISO && fechaISO < desdeISO) {
          return false
        }
        if (hastaISO && fechaISO > hastaISO) {
          return false
        }
      }

      if (precioDesde || precioHasta) {
        const precioTotal = p.pedidoItems.reduce(
          (total, e) =>
            total + e.precioUnitario * e.cantidad * (1 + (e.alicuota || 0) / 100),
          0
        )

        const desde = Number(precioDesde)
        const hasta = Number(precioHasta)

        if (desde && precioTotal < desde) {
          return false
        }
        if (hasta && precioTotal > hasta) {
          return false
        }
      }
      if (valorBusqueda) {
        const items = Array.isArray(p.pedidoItems) ? p.pedidoItems : []
        const tituloMatch = items.some((item) =>
          (item?.titulo || '').toLowerCase().includes(valorBusqueda)
        )

        const usuarioMatch = esVentas
          ? items.some((item) =>
              (item?.compradorNombre || '').toLowerCase().includes(valorBusqueda)
            )
          : items.some((item) =>
              (item?.vendedorNombre || '').toLowerCase().includes(valorBusqueda)
            )

        const idMatch = p.idPedido.toString().includes(valorBusqueda)

        if (!tituloMatch && !usuarioMatch && !idMatch) {
          return false
        }
      }

      return true
    })
    setPaginaActual(1)
    setPedidos(pedidosFiltrados)
    setSinResultados(pedidosFiltrados.length === 0)
  }

  useEffect(() => {
    aplicarFiltros()
  }, [filtros, fechaDesde, fechaHasta, precioDesde, precioHasta, textoBusqueda])

  const hoy = Date.now()

  const handleFechaDesdeChange = (value) => {
    if (!value) {
      setFechaDesde('')
      return ''
    }

    const desde = new Date(value).getTime()
    const hasta = fechaHasta ? new Date(fechaHasta).getTime() : null

    if ((hasta && desde > hasta) || desde > hoy) {
      setFechaDesde('')
    } else {
      setFechaDesde(value)
    }
  }

  const handleFechaHastaChange = (value) => {
    if (!value) {
      setFechaHasta(null)
      return ''
    }

    const hasta = new Date(value).getTime()
    const desde = fechaDesde ? new Date(fechaDesde).getTime() : null

    if ((desde && hasta < desde) || hasta > hoy) {
      setFechaHasta(null)
    } else {
      setFechaHasta(value)
    }
  }

  const limpiarFiltros = () => {
    setTextoBusqueda('')
    setFiltros({
      filtroVendedor: null,
      filtroEstado: null,
      filtroComprador: null
    })
    setFechaDesde(null)
    setFechaHasta(null)
    dispatch(setPrecio({ desde: '', hasta: '' }))
    setPedidos(pedidosOriginales)
    setSinResultados(pedidosOriginales.length === 0)
  }

  useEffect(() => {
    limpiarFiltros()
  }, [esVentas])

  return (
    <div>
      <BordePrincipal titulo={esVentas ? 'Mis Ventas' : 'Mis Compras'}>
        <div className={styles.layout}>
          <aside className={styles.filtros}>
            <Filtros
              filtrosFijos={filtrosFijos}
              buscador={{
                onChange: () => {},
                placeholder: 'Buscar por título o NRO pedido...',
                valor: textoBusqueda,
                setBusqueda: setTextoBusqueda
              }}
              mostrarFiltroPrecio={true}
              mostrarLimpiarFiltros={
                fechaDesde ||
                fechaHasta ||
                Object.values(filtros).some((v) => v !== null) ||
                textoBusqueda
              }
              fechaSeleccionada={[
                {
                  fecha: fechaDesde,
                  onChange: (value) => handleFechaDesdeChange(value),
                  label: 'Compra desde'
                },
                {
                  fecha: fechaHasta,
                  onChange: (value) => handleFechaHastaChange(value),
                  label: 'Compra hasta'
                }
              ]}
              limpiarFiltros={limpiarFiltros}
            />
          </aside>
          <div className="col">
            {!sinResultados ? (
              pedidosPaginados.map((pedido) => (
                <Pedido
                  key={pedido.idPedido}
                  pedido={pedido}
                  esVentas={esVentas}
                  mediosPago={mediosPago}
                />
              ))
            ) : !textoBusqueda ? (
              esVentas ? (
                <div className="row">
                  <ComponenteError tipo="I" idAviso={22} />
                </div>
              ) : (
                <div className="row">
                  <ComponenteError tipo="I" idAviso={10} />
                </div>
              )
            ) : (
              <div className="row">
                <ComponenteError tipo="I" idAviso={24} />
              </div>
            )}
          </div>
        </div>
      </BordePrincipal>
      {totalPaginas > 1 && (
        <Paginacion
          totalPaginas={totalPaginas}
          paginaActual={paginaActual}
          onCambioPagina={(num) => {
            setPaginaActual(num)
            topRef.current?.scrollIntoView({ behavior: 'smooth' })
          }}
          itemsPorPagina={itemsPorPagina}
          onCambioItemsPorPagina={(num) => {
            setItemsPorPagina(num)
            setPaginaActual(1)
          }}
        />
      )}
    </div>
  )
}

export default Pedidos

Pedidos.propTypes = {
  esVentas: PropTypes.bool.isRequired
}
